import type { TFunction } from 'i18next'
import type {
  ExtractionProgressStep,
  ExtractionProgressStepStatus,
} from '@/components/extraction-progress-checklist'
import {
  DISCOVERY_ORDERED_STEPS,
  type DiscoveryStepId,
  type DiscoveryTaskStatus,
  type LlmTrackState,
  type StandardApplicabilityDiscoveryTaskState,
} from './standardApplicabilityDiscoveryTaskState'

const STEP_I18N_KEY: Record<DiscoveryStepId, string> = {
  get_applicability_context: 'getApplicabilityContext',
  discover_integrated_components: 'discoverIntegrated',
  retrieve_type_c_standards: 'retrieveTypeC',
  llm_applicability: 'llmApplicability',
  resolve_mandatory_iso_12100: 'resolveIso12100',
  persist_applicability: 'persist',
}

function mapDiscoveryToExtraction(
  status: DiscoveryTaskStatus
): ExtractionProgressStepStatus {
  if (status === 'pending') {
    return 'pending'
  }
  if (status === 'active') {
    return 'active'
  }
  if (status === 'done') {
    return 'done'
  }
  return 'pending'
}

function trackSubtitle(
  track: LlmTrackState,
  translate: TFunction<'machineryDirectiveCompliance', undefined>,
  candidateCount: number | null,
  llmStep: DiscoveryTaskStatus
): string {
  if (
    track.phase === 'idle' &&
    llmStep === 'done' &&
    candidateCount !== null &&
    candidateCount === 0
  ) {
    return translate(
      'onboarding.standardApplicabilityDiscovery.llm.skippedNoCandidates'
    )
  }
  if (track.phase === 'running') {
    return translate(
      'onboarding.standardApplicabilityDiscovery.llm.batchProgress',
      {
        current: track.batchIndex,
        total: track.batchTotal,
      }
    )
  }
  if (track.phase === 'done') {
    return translate('onboarding.standardApplicabilityDiscovery.llm.done')
  }
  return translate('onboarding.standardApplicabilityDiscovery.llm.waiting')
}

function trackToExtractionStatus(
  track: LlmTrackState,
  llmStep: DiscoveryTaskStatus,
  candidateCount: number | null
): ExtractionProgressStepStatus {
  if (track.phase === 'done') {
    return 'done'
  }
  if (track.phase === 'running') {
    return 'active'
  }
  if (llmStep === 'done') {
    if (
      track.phase === 'idle' &&
      candidateCount !== null &&
      candidateCount === 0
    ) {
      return 'pending'
    }
    return 'done'
  }
  if (llmStep === 'active') {
    return 'waiting'
  }
  return 'pending'
}

function buildRetrieveSearchRow(params: {
  stepId: 'retrieve_type_c_standards'
  titleKey: 'retrieveTypeC'
  candidateCount: number | null
  searchStepStatus: DiscoveryTaskStatus
  analyzeTitleKey: 'analyzingApplicabilityTypeC'
  track: LlmTrackState
  llmStepStatus: DiscoveryTaskStatus
  translate: TFunction<'machineryDirectiveCompliance', undefined>
}): ExtractionProgressStep {
  const {
    stepId,
    titleKey,
    candidateCount,
    searchStepStatus,
    analyzeTitleKey,
    track,
    llmStepStatus,
    translate,
  } = params

  let subtitle: string | undefined
  if (
    searchStepStatus === 'done' &&
    candidateCount !== null
  ) {
    subtitle = translate(
      'onboarding.standardApplicabilityDiscovery.foundCandidates',
      {
        count: candidateCount,
      }
    )
  }

  return {
    id: stepId,
    title: translate(
      `onboarding.standardApplicabilityDiscovery.steps.${titleKey}`
    ),
    subtitle,
    status: mapDiscoveryToExtraction(searchStepStatus),
    children: [
      {
        id: `${stepId}-analyze-applicability`,
        title: translate(
          `onboarding.standardApplicabilityDiscovery.${analyzeTitleKey}`
        ),
        subtitle: trackSubtitle(track, translate, candidateCount, llmStepStatus),
        status: trackToExtractionStatus(
          track,
          llmStepStatus,
          candidateCount
        ),
      },
    ],
  }
}

const PREAMBLE_STEP_IDS = new Set<string>([
  'get_search_query_and_language',
  'discover_integrated_components',
])

export type StandardApplicabilityStepPartition = {
  preamble: ExtractionProgressStep[]
  retrieveTypeC: ExtractionProgressStep | undefined
  resolveIso: ExtractionProgressStep | undefined
  persist: ExtractionProgressStep | undefined
}

export function partitionStandardApplicabilityExtractionSteps(
  steps: ExtractionProgressStep[]
): StandardApplicabilityStepPartition {
  const preamble = steps.filter((step) => PREAMBLE_STEP_IDS.has(step.id))
  let retrieveTypeC: ExtractionProgressStep | undefined
  let resolveIso: ExtractionProgressStep | undefined
  let persist: ExtractionProgressStep | undefined
  for (const step of steps) {
    if (step.id === 'retrieve_type_c_standards') {
      retrieveTypeC = step
    }
    if (step.id === 'resolve_mandatory_iso_12100') {
      resolveIso = step
    }
    if (step.id === 'persist_applicability') {
      persist = step
    }
  }
  return { preamble, retrieveTypeC, resolveIso, persist }
}

/**
 * Maps workflow discovery state to the same checklist shape as Lex product extraction
 * (`ExtractionProgressChecklist`). Type B/C applicability analysis appears nested under
 * the matching library search step.
 */
export function buildStandardApplicabilityExtractionSteps(
  state: StandardApplicabilityDiscoveryTaskState,
  translate: TFunction<'machineryDirectiveCompliance', undefined>
): ExtractionProgressStep[] {
  const steps: ExtractionProgressStep[] = []
  const llmStepStatus = state.steps.llm_applicability

  for (const stepId of DISCOVERY_ORDERED_STEPS) {
    if (
      stepId === 'discover_integrated_components' &&
      state.steps[stepId] === 'skipped'
    ) {
      continue
    }

    if (stepId === 'llm_applicability') {
      continue
    }

    if (stepId === 'retrieve_type_c_standards') {
      steps.push(
        buildRetrieveSearchRow({
          stepId: 'retrieve_type_c_standards',
          titleKey: 'retrieveTypeC',
          candidateCount: state.retrieveTypeCCandidateCount,
          searchStepStatus: state.steps.retrieve_type_c_standards,
          analyzeTitleKey: 'analyzingApplicabilityTypeC',
          track: state.llmTypeC,
          llmStepStatus: llmStepStatus,
          translate,
        })
      )
      continue
    }

    steps.push({
      id: stepId,
      title: translate(
        `onboarding.standardApplicabilityDiscovery.steps.${STEP_I18N_KEY[stepId]}`
      ),
      status: mapDiscoveryToExtraction(state.steps[stepId]),
    })
  }
  return steps
}
